import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Rewrites a storage URL so that Next.js image optimisation can fetch it
 * server-side when running inside Docker.
 *
 * The API stores images with the *public* MinIO URL (e.g. http://localhost:9000).
 * Inside the FO Docker container `localhost` resolves to the container itself,
 * not MinIO, so `next/image` gets a connection refused / 500.
 *
 * Set STORAGE_URL=http://minio:9000 in the FO service environment to enable
 * the rewrite.  On the client, URLs are returned unchanged (the browser never
 * fetches the source directly — it always goes through /_next/image).
 */
export function resolveStorageUrl(url: string): string {
  if (!url) return url;
  // Client-side: leave URL as-is; Next.js image optimizer runs server-side.
  if (typeof window !== 'undefined') return url;
  const storageUrl = process.env.STORAGE_URL;
  if (!storageUrl) return url;
  try {
    const src = new URL(url);
    // Only rewrite localhost/127.0.0.1 URLs — never touch CDN or external URLs.
    if (src.hostname !== 'localhost' && src.hostname !== '127.0.0.1') return url;
    const internal = new URL(storageUrl);
    src.protocol = internal.protocol;
    src.hostname = internal.hostname;
    src.port = internal.port;
    return src.toString();
  } catch {
    return url;
  }
}

export function formatPrice(price: number): string {
  return new Intl.NumberFormat('fr-MA', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price) + ' MAD';
}

export function formatDate(date: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(date));
}

// ─── Property type groups ──────────────────────────────────────────────────

export interface PropertyTypeValue {
  key: string;
  label: string;
}

export interface PropertyTypeGroup {
  group_key: string;
  group_label: string;
  available_for: Array<'sale' | 'rent'>;
  values: PropertyTypeValue[];
}

export const PROPERTY_TYPE_GROUPS: PropertyTypeGroup[] = [
  {
    group_key: 'residential',
    group_label: 'Immobilier Résidentiel',
    available_for: ['sale', 'rent'],
    values: [
      { key: 'apartment', label: 'Appartement' },
      { key: 'villa', label: 'Villa' },
      { key: 'maison', label: 'Maison' },
      { key: 'studio', label: 'Studio' },
      { key: 'duplex', label: 'Duplex' },
      { key: 'penthouse', label: 'Penthouse' },
    ],
  },
  {
    group_key: 'touristic',
    group_label: 'Établissement touristique',
    available_for: ['sale', 'rent'],
    values: [
      { key: 'riad', label: 'Riad' },
      { key: 'hotel', label: 'Hôtel' },
      { key: 'maison_hote', label: "Maison d'hôte" },
      { key: 'residence_touristique', label: 'Résidence touristique' },
      { key: 'auberge', label: 'Auberge' },
    ],
  },
  {
    group_key: 'commercial',
    group_label: 'Immobilier Commercial',
    available_for: ['sale', 'rent'],
    values: [
      { key: 'bureau', label: 'Bureau' },
      { key: 'commercial', label: 'Local commercial' },
      { key: 'magasin', label: 'Magasin' },
      { key: 'entrepot', label: 'Entrepôt' },
    ],
  },
  {
    group_key: 'fonds_commerce',
    group_label: 'Fonds de commerce',
    available_for: ['sale', 'rent'],
    values: [
      { key: 'cafe', label: 'Café' },
      { key: 'snack', label: 'Snack' },
      { key: 'restaurant', label: 'Restaurant' },
      { key: 'boulangerie', label: 'Boulangerie' },
    ],
  },
  {
    group_key: 'terrain',
    group_label: 'Terrain',
    available_for: ['sale'],
    values: [
      { key: 'terrain_urbain', label: 'Terrain urbain' },
      { key: 'terrain_rural', label: 'Terrain rural' },
      { key: 'terrain_agricole', label: 'Terrain agricole' },
    ],
  },
];

export const PROPERTY_TYPE_LABEL_MAP: Record<string, string> = Object.fromEntries(
  PROPERTY_TYPE_GROUPS.flatMap((g) => g.values.map((v) => [v.key, v.label])),
);

export function getPropertyTypeLabel(type: string): string {
  return PROPERTY_TYPE_LABEL_MAP[type] || type;
}

export function getTransactionTypeLabel(type: string): string {
  return type === 'sale' ? 'Vente' : 'Location';
}

export function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    draft: 'Brouillon',
    pending: 'En attente',
    active: 'Active',
    sold: 'Vendu',
    rented: 'Loué',
    rejected: 'Rejeté',
    expired: 'Expiré',
  };
  return labels[status] || status;
}

export function getPropertySlugUrl(type: string, citySlug: string, transactionType: string): string {
  const base = transactionType === 'sale' ? 'acheter' : 'louer';
  const typeSlug = type.toLowerCase();
  return `/${base}/${typeSlug}/${citySlug}`;
}

/**
 * A 1×1 px translucent shimmer encoded as a base64 data URI.
 * Used as `blurDataURL` on every next/image with `placeholder="blur"` so
 * images never flash a blank box — they fade in from a neutral tint that
 * matches the design system surface colours.
 */
export const BLUR_DATA_URL =
  'data:image/webp;base64,UklGRlYAAABXRUJQVlA4IEoAAADQAQCdASoBAAEAAUAmJZACdAEO/gBOAAD++1T/sW7/yHT/gPID/gD/AAAAAA==';


